/**
 * ReferralItem Component
 *
 * Individual referral item component for displaying referral information
 * in a card format. Features status badges, patient information, dates,
 * and accessibility support with keyboard navigation.
 *
 * @module components/provider-dashboard/ReferralItem
 */

import React from 'react';
import { Card } from '@/components/ui/Card';
import { AvatarPhoto } from '@/components/ui/AvatarPhoto';
import { ReferralStatusBadge } from '@/components/ui/ReferralStatusBadge';
import { useTheme } from '@/hooks/useTheme';
import type { ReferralItemProps } from '@/types/dashboard';
import { cn } from '@/utils/cn';


/**
 * Individual referral item component
 */
export const ReferralItem: React.FC<ReferralItemProps> = ({
  referral,
  photoUrl,
  onClick,
  className,
}) => {
  const { getTextColorClass, getThemeClasses } = useTheme();

  /**
   * Handle click events
   */
  const handleClick = () => {
    onClick(referral.id);
  };

  /**
   * Handle keyboard events
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <Card
      className={cn(
        'p-4 cursor-pointer transition-all duration-200',
        'hover:shadow-md',
        getThemeClasses(
          'hover:border-blue-300 focus-within:ring-blue-500',
          'hover:border-blue-600 focus-within:ring-blue-500'
        ),
        'focus-within:ring-2 focus-within:ring-offset-2',
        className
      )}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={`View referral for ${referral.patientName}`}
    >
      <div className="flex justify-between">
        {/* Avatar and main content */}
        <div className="flex flex-1 min-w-0">
          {/* Patient Avatar - aligned with name */}
          <div className="flex-shrink-0 mr-3">
            <AvatarPhoto
              src={photoUrl}
              firstName={referral.patientName.split(' ')[0]}
              lastName={referral.patientName.split(' ').slice(1).join(' ')}
              size="sm"
            />
          </div>

          {/* Patient info */}
          <div className="flex-1 min-w-0">
            {/* Patient name */}
            <h3 className={cn("text-lg font-semibold truncate mb-2", getTextColorClass('primary'))}>
              {referral.patientName}
            </h3>

            {/* Status and Conditions - responsive layout */}
            <div className="flex flex-col md:flex-row md:items-center md:gap-3">
              {/* Status badge */}
              <div className="mb-2 md:mb-0">
                <ReferralStatusBadge status={referral.status} />
              </div>
            </div>
          </div>
        </div>

        {/* Right chevron indicator - aligned with content */}
        <div className="flex-shrink-0 self-start mt-1">
          <div className={getTextColorClass('muted')} aria-hidden="true">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ReferralItem;